/**
 * dbcsi_to_pricing_calculator.js
 *
 * Converts a DBCSI scenario JSON items[] array into AWS Pricing Calculator
 * export-style JSON objects for RDS-Oracle and Aurora PostgreSQL.
 *
 * Usage (as a module):
 *   import { convertToPricingCalculator, convertToPricingCalculatorAurora } from './dbcsi_to_pricing_calculator.js';
 *   const rdsResult = convertToPricingCalculator(items, 'My RDS Estimate');
 *   const auroraResult = convertToPricingCalculatorAurora(items, 'My Aurora Estimate');
 */

const STORAGE_TYPE_LABELS = {
  gp3: 'General Purpose SSD (gp3)',
  io1: 'Provisioned IOPS SSD (io1)',
  io2: 'Provisioned IOPS SSD (io2)',
};

const DB_EDITION = {
  ee:  'Enterprise',
  se:  'Standard Edition Two',
  se2: 'Standard Edition Two',
};

/**
 * Converts a DBCSI items[] array into a Pricing Calculator JSON object.
 *
 * @param {Array}  items       - The items[] array from a DBCSI scenario JSON file.
 * @param {string} name        - The estimate name for the top-level "Name" field.
 * @param {string} [createdOn] - Optional date string (e.g. "4/23/2026"). Defaults to today.
 * @param {string} [source]    - Optional source description for Metadata.
 * @returns {Object} Pricing Calculator JSON object.
 */
function convertToPricingCalculator(items, name, createdOn, source) {
  const dateStr = createdOn || todayStr();

  const services = [];
  let totalMonthly = 0;

  for (const item of items) {
    const rds = item.targetconfig_rds;
    if (!rds) continue;

    const monthly = parseFloat(rds.monthly_estimate);
    if (isNaN(monthly)) continue;

    totalMonthly += monthly;
    const annual = monthly * 12;

    // Deployment option
    const deployment = rds.UseMAZ === 'Yes' ? 'Multi-AZ' : 'Single-AZ';

    // Database edition
    const dbEdition = DB_EDITION[rds.rds_type] ?? 'Enterprise';

    // Pricing strategy
    const pricingStrategy = rds.RIused === 'RI' ? 'Reserved 1yr No Upfront' : 'OnDemand';

    // Storage type label
    const ebsType = rds.ebs_data_type || 'gp3';
    const storageTypeLabel = STORAGE_TYPE_LABELS[ebsType] ?? ebsType;

    // Instance type — ensure db. prefix
    const shape = rds.shape || '';
    const instanceType = shape.startsWith('db.') ? shape : `db.${shape}`;

    // Throughput — fall back to 125 if missing
    const throughput = rds.ebs_burst_Throughput || 125;

    // Build properties
    const isLicenseIncluded = rds.rds_type === 'se' || rds.rds_type === 'se2';
    const properties = {
      'Storage amount': `${rds.ebs_data_size} GB`,
      'Would you be enabling Database Insights with the database?': 'No',
      'Nodes': String(rds.rac_nodes ?? 1),
      'Instance type': instanceType,
      'Utilization (On-Demand only)': '100 %Utilized/Month',
      'Deployment option': deployment,
      'License': isLicenseIncluded ? 'License included' : 'Bring your own license',
      'Database edition': dbEdition,
      'Pricing strategy': pricingStrategy,
      'Storage for each RDS instance': storageTypeLabel,
    };

    // Storage-type-specific properties
    if (ebsType === 'gp3') {
      properties['General Purpose SSD (gp3) - IOPS'] = String(rds.ebs_data_iops ?? 3000);
      properties['General Purpose SSD (gp3) - Throughput'] = `${throughput} MiBps`;
    } else if (ebsType === 'io1' || ebsType === 'io2') {
      properties[`Provisioned IOPS ${ebsType.toUpperCase()}`] = String(rds.ebs_data_iops ?? 1000);
    }

    services.push({
      'Service Name': 'Amazon RDS for Oracle',
      'Region': 'US East (N. Virginia)',
      'Status': '',
      'Service Cost': {
        'monthly':    monthly.toFixed(2),
        'upfront':    '0.00',
        '12 months':  annual.toFixed(2),
      },
      'Properties': properties,
      // Traceability fields (beyond standard Pricing Calculator format)
      'dbcsi_database':    item.database ?? '',
      'dbcsi_summaryText': rds.summaryText ?? '',
    });
  }

  const totalAnnual = totalMonthly * 12;

  return {
    'Name': name,
    'Total Cost': {
      'monthly':   totalMonthly.toFixed(2),
      'upfront':   '0.00',
      '12 months': totalAnnual.toFixed(2),
    },
    'Metadata': buildMetadata(dateStr, source, 'Generated from DBCSI targetconfig_rds data'),
    'Groups': {
      'Services': services,
    },
  };
}

function buildMetadata(dateStr, source, defaultSource) {
  return {
    'Currency':         'USD',
    'Locale':           'en_US',
    'Created On':       dateStr,
    'Source':           source ?? defaultSource,
    'Legal Disclaimer': 'This estimate was generated from DBCSI sizing data and does not include any taxes that might apply. Your actual fees depend on a variety of factors, including your actual usage of AWS services.',
  };
}

function todayStr() {
  const today = new Date();
  return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
}

/**
 * Converts a DBCSI items[] array into a Pricing Calculator JSON for Aurora PostgreSQL.
 *
 * @param {Array}  items       - The items[] array from a DBCSI scenario.
 * @param {string} name        - The estimate name.
 * @param {string} [createdOn] - Optional date string. Defaults to today.
 * @param {string} [source]    - Optional source description.
 * @returns {Object} Pricing Calculator JSON object.
 */
function convertToPricingCalculatorAurora(items, name, createdOn, source) {
  const dateStr = createdOn || todayStr();
  const services = [];
  let totalMonthly = 0;

  for (const item of items) {
    const aurora = item.targetconfig_aurora;
    if (!aurora || aurora.shape === 'N/A' || aurora.shape == null) continue;

    const monthly = parseFloat(aurora.monthly_estimate);
    if (isNaN(monthly)) continue;

    totalMonthly += monthly;
    const annual = monthly * 12;

    const shape = aurora.shape || '';
    const instanceType = shape.startsWith('db.') ? shape : `db.${shape}`;
    const isIOOptimized = aurora.iops_summaryText2 === 'Aurora IO-Optimized mode';

    let properties;
    if (isIOOptimized) {
      properties = {
        'Aurora PostgreSQL Cluster Configuration Option': 'Aurora I/O-Optimized',
        'Nodes': '1',
        'Instance Type': instanceType,
        'Utilization': '100 %Utilized/Month',
        'Pricing Model': 'OnDemand',
        'Storage amount': `${aurora.ebs_data_size} GB`,
      };
    } else {
      properties = {
        'Aurora PostgreSQL Cluster Configuration Option': 'Aurora Standard',
        'Quantity': '1',
        'Instance type': instanceType,
        'Utilization': '100 %Utilized/Month',
        'Pricing strategy': 'OnDemand',
        'Storage amount': `${aurora.ebs_data_size} GB`,
      };
    }

    services.push({
      'Service Name': 'Amazon Aurora PostgreSQL-Compatible DB',
      'Region': 'US East (N. Virginia)',
      'Status': '',
      'Service Cost': {
        'monthly':   monthly.toFixed(2),
        'upfront':   '0.00',
        '12 months': annual.toFixed(2),
      },
      'Properties': properties,
      'dbcsi_database':    item.database ?? '',
      'dbcsi_summaryText': aurora.summaryText ?? '',
    });
  }

  const totalAnnual = totalMonthly * 12;

  return {
    'Name': name,
    'Total Cost': {
      'monthly':   totalMonthly.toFixed(2),
      'upfront':   '0.00',
      '12 months': totalAnnual.toFixed(2),
    },
    'Metadata': buildMetadata(dateStr, source, 'Generated from DBCSI targetconfig_aurora data'),
    'Groups': {
      'Services': services,
    },
  };
}

export { convertToPricingCalculator, convertToPricingCalculatorAurora };
